// Column sorting for the admin Dashboard's Waves / Summary tables
// (Alex, 2026-09-10): every header is a toggle. The first click on a
// column sorts the way its data reads naturally — text A→Z, numbers
// (and times) highest / latest first — and the next click flips it.
// Pure functions so the order is unit-testable without the component.

export type SortDir = "asc" | "desc";
export type SortKind = "text" | "number";

export interface SortState {
    col: string;
    dir: SortDir;
}

/** One sortable column: its header i18n key, how its values compare,
 *  and how to read the value off a row. */
export interface ColumnSpec<R> {
    key: string;
    kind: SortKind;
    value: (row: R) => string | number | null | undefined;
    /** Optional width class for the <th> (dash-th-user, dash-th-md…). */
    className?: string;
}

export const defaultDir = (kind: SortKind): SortDir => (kind === "number" ? "desc" : "asc");

/** Next state after clicking `col`: a new column starts at its natural
 *  direction; the same column again flips. */
export const nextSort = (prev: SortState | null | undefined, col: ColumnSpec<never>): SortState => {
    if (prev && prev.col === col.key) {
        return { col: col.key, dir: prev.dir === "asc" ? "desc" : "asc" };
    }
    return { col: col.key, dir: defaultDir(col.kind) };
};

/** Epoch ms for an ISO timestamp, null when absent / unparseable —
 *  time columns sort as numbers so "highest first" = latest first. */
export const timeMs = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
};

const isBlank = (v: string | number | null | undefined) =>
    v === null || v === undefined || v === "";

/** Stable sort of `rows` by the column named in `state` (unsorted copy
 *  when there is no state or the column is unknown). Blank values sit
 *  last whichever way the column is sorted. */
export const sortRows = <R,>(rows: R[], cols: ColumnSpec<R>[], state: SortState | null | undefined): R[] => {
    if (!state) return rows;
    const col = cols.find((c) => c.key === state.col);
    if (!col) return rows;
    const sign = state.dir === "asc" ? 1 : -1;
    return rows
        .map((row, i) => ({ row, i, v: col.value(row) }))
        .sort((a, b) => {
            const aBlank = isBlank(a.v);
            const bBlank = isBlank(b.v);
            if (aBlank && bBlank) return a.i - b.i;
            if (aBlank) return 1;
            if (bBlank) return -1;
            let cmp: number;
            if (col.kind === "number") {
                cmp = Number(a.v) - Number(b.v);
            } else {
                cmp = String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: "base" });
            }
            return cmp !== 0 ? cmp * sign : a.i - b.i;
        })
        .map((x) => x.row);
};
